import { create } from 'zustand';
import { resolutionApi, type RawConceptData } from '@/services/api';

interface ConceptsFilters {
  entityType: string;
  resolutionStatus: string;
  clientUid: string;
  researchId: string;
  search: string;
}

interface ConceptsState {
  concepts: RawConceptData[];
  selectedConcept: RawConceptData | null;
  filters: ConceptsFilters;

  loading: boolean;
  detailLoading: boolean;
  resolving: boolean;
  error: string | null;

  setFilters: (filters: Partial<ConceptsFilters>) => void;
  selectConcept: (concept: RawConceptData | null) => void;
  fetchConcepts: () => Promise<void>;
  fetchConceptDetail: (id: string) => Promise<void>;
  triggerResolution: (id: string) => Promise<void>;
}

const PAGE_SIZE = 100;

export const useConceptsStore = create<ConceptsState>((set, get) => ({
  concepts: [],
  selectedConcept: null,
  filters: {
    entityType: '',
    resolutionStatus: '',
    clientUid: '',
    researchId: '',
    search: '',
  },

  loading: false,
  detailLoading: false,
  resolving: false,
  error: null,

  setFilters: (partial) => {
    set((s) => ({ filters: { ...s.filters, ...partial } }));
    // Search filters server-side only on structured fields; the free-text box
    // is a name-contains filter applied client-side in the list component.
    const structuralChange = Object.keys(partial).some(
      (k) => k !== 'search',
    );
    if (structuralChange) get().fetchConcepts();
  },

  selectConcept: (concept) => {
    set({ selectedConcept: concept });
  },

  fetchConcepts: async () => {
    const { filters } = get();
    set({ loading: true, error: null });
    try {
      const res = await resolutionApi.listConcepts({
        entity_type: filters.entityType || undefined,
        resolution_status: filters.resolutionStatus || undefined,
        client_uid: filters.clientUid || undefined,
        research_id: filters.researchId || undefined,
        limit: PAGE_SIZE,
      });
      set({ concepts: res.concepts, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchConceptDetail: async (id) => {
    set({ detailLoading: true });
    try {
      const concept = await resolutionApi.getConcept(id);
      set({ selectedConcept: concept, detailLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, detailLoading: false });
    }
  },

  triggerResolution: async (id) => {
    set({ resolving: true });
    try {
      const res = await resolutionApi.resolveConcept(id);
      // Update the inline row optimistically — if already resolved, the API
      // returns the canonical_entity_id and we reflect that in the list.
      if (res.status === 'already_resolved' && res.canonical_entity_id) {
        set((s) => ({
          concepts: s.concepts.map((c) =>
            c.id === id
              ? {
                  ...c,
                  resolution_status: 'resolved',
                  canonical_entity_id: res.canonical_entity_id ?? c.canonical_entity_id,
                }
              : c,
          ),
          selectedConcept:
            s.selectedConcept?.id === id
              ? {
                  ...s.selectedConcept,
                  resolution_status: 'resolved',
                  canonical_entity_id:
                    res.canonical_entity_id ?? s.selectedConcept.canonical_entity_id,
                }
              : s.selectedConcept,
        }));
      } else {
        // Scheduled: mark as in-progress locally; a real resolution flip happens
        // asynchronously on the backend. User can refresh for latest state.
        set((s) => ({
          concepts: s.concepts.map((c) =>
            c.id === id ? { ...c, resolution_status: 'pending' } : c,
          ),
        }));
      }
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ resolving: false });
    }
  },
}));
